import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Save, X, Book, Plus } from 'lucide-react';
import { MealOption } from '../types';
import { toast } from 'react-hot-toast';
import {
  useMealOptions,
  useCreateMealOption,
  useUpdateMealOption,
  useDeleteMealOption,
} from '@/hooks/useMealOptions';

interface MealOptionManagerProps {
  mealOptions?: MealOption[]; // Now optional - will fetch if not provided
  onUpdate?: () => void; // Now optional - mutations handle cache
  isParent: boolean;
  startCreating?: boolean; // Start with create form open
}

export const MealOptionManager: React.FC<MealOptionManagerProps> = ({
  mealOptions: propMealOptions,
  onUpdate,
  isParent,
  startCreating = false
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<MealOption>>({});
  const [showRecipe, setShowRecipe] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(startCreating);
  const [newMealForm, setNewMealForm] = useState<Partial<MealOption>>({
    emoji: '🍽️',
    name: '',
    description: '',
    meal_type: 'dinner',
    category: 'home_cooked',
    prep_time_minutes: 30,
    recipe: '',
  });

  // Respond to startCreating prop changes
  useEffect(() => {
    if (startCreating) {
      setIsCreating(true);
    }
  }, [startCreating]);

  // Use React Query hooks
  const { data: queryMealOptions = [] } = useMealOptions();
  const createMutation = useCreateMealOption();
  const updateMutation = useUpdateMealOption();
  const deleteMutation = useDeleteMealOption();

  // Use prop data if provided, otherwise use query data
  const mealOptions = propMealOptions ?? queryMealOptions;

  const handleEdit = (option: MealOption) => {
    setEditingId(option.id);
    setEditForm({
      ...option,
      recipe: option.recipe || ''
    });
  };

  const handleSave = async () => {
    if (!editingId) return;

    try {
      await updateMutation.mutateAsync({ mealOptionId: editingId, updates: editForm });
      toast.success('Meal option updated');
      setEditingId(null);
      onUpdate?.(); // Call if provided for backwards compatibility
    } catch (error) {
      toast.error('Failed to update meal option');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this meal option?')) return;

    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Meal option deleted');
      onUpdate?.(); // Call if provided for backwards compatibility
    } catch (error) {
      toast.error('Failed to delete meal option');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleCreate = async () => {
    if (!newMealForm.name?.trim()) {
      toast.error('Please enter a meal name');
      return;
    }

    try {
      await createMutation.mutateAsync(newMealForm as MealOption);
      toast.success('Meal option created');
      setIsCreating(false);
      setNewMealForm({
        emoji: '🍽️',
        name: '',
        description: '',
        meal_type: 'dinner',
        category: 'home_cooked',
        prep_time_minutes: 30,
        recipe: '',
      });
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to create meal option');
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewMealForm({
      emoji: '🍽️',
      name: '',
      description: '',
      meal_type: 'dinner',
      category: 'home_cooked',
      prep_time_minutes: 30,
      recipe: '',
    });
  };

  // Emoji tooltips
  const getCategoryTooltip = (category: string) => {
    const tooltips: Record<string, string> = {
      home_cooked: 'Home Cooked',
      takeout: 'Takeout',
      restaurant: 'Restaurant',
      delivery: 'Delivery'
    };
    return tooltips[category] || category;
  };

  const getMealTypeTooltip = (type: string) => {
    const tooltips: Record<string, string> = {
      breakfast: 'Breakfast',
      lunch: 'Lunch', 
      dinner: 'Dinner',
      snack: 'Snack'
    };
    return tooltips[type] || type;
  };

  return (
    <div className="space-y-4">
      {/* Add New Meal Button / Form */}
      {isParent && (
        <div className="border rounded-lg p-4 bg-white">
          {isCreating ? (
            <div className="space-y-3">
              <h3 className="font-semibold text-lg mb-3">Add New Meal</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMealForm.emoji || ''}
                  onChange={(e) => setNewMealForm({...newMealForm, emoji: e.target.value})}
                  className="w-12 p-2 border rounded text-center"
                  placeholder="🍽️"
                />
                <input
                  type="text"
                  value={newMealForm.name || ''}
                  onChange={(e) => setNewMealForm({...newMealForm, name: e.target.value})}
                  className="flex-1 p-2 border rounded"
                  placeholder="Meal name"
                  autoFocus
                />
              </div>

              <textarea
                value={newMealForm.description || ''}
                onChange={(e) => setNewMealForm({...newMealForm, description: e.target.value})}
                className="w-full p-2 border rounded"
                placeholder="Description (optional)"
                rows={2}
              />

              <div className="grid grid-cols-2 gap-2">
                <select
                  value={newMealForm.meal_type || 'dinner'}
                  onChange={(e) => setNewMealForm({...newMealForm, meal_type: e.target.value})}
                  className="p-2 border rounded"
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="snack">Snack</option>
                </select>

                <select
                  value={newMealForm.category || 'home_cooked'}
                  onChange={(e) => setNewMealForm({...newMealForm, category: e.target.value})}
                  className="p-2 border rounded"
                >
                  <option value="home_cooked">Home Cooked</option>
                  <option value="takeout">Takeout</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="delivery">Delivery</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  value={newMealForm.prep_time_minutes || ''}
                  onChange={(e) => setNewMealForm({...newMealForm, prep_time_minutes: parseInt(e.target.value)})}
                  className="p-2 border rounded"
                  placeholder="Prep time (minutes)"
                />
                <input
                  type="number"
                  value={newMealForm.cost_estimate || ''}
                  onChange={(e) => setNewMealForm({...newMealForm, cost_estimate: parseFloat(e.target.value)})}
                  className="p-2 border rounded"
                  placeholder="Cost estimate ($)"
                  step="0.01"
                />
              </div>

              <textarea
                value={newMealForm.recipe || ''}
                onChange={(e) => setNewMealForm({...newMealForm, recipe: e.target.value})}
                className="w-full p-2 border rounded"
                placeholder="Recipe instructions (optional)"
                rows={4}
              />

              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleCancelCreate}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {createMutation.isPending ? 'Saving...' : 'Save Meal'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-400 hover:text-orange-600 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add New Meal
            </button>
          )}
        </div>
      )}

      {/* Existing Meals List */}
      {mealOptions.map(option => (
        <div key={option.id} className="border rounded-lg p-4 bg-white">
          {editingId === option.id ? (
            // Edit mode
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editForm.emoji || ''}
                  onChange={(e) => setEditForm({...editForm, emoji: e.target.value})}
                  className="w-12 p-2 border rounded"
                  placeholder="🍽️"
                />
                <input
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="flex-1 p-2 border rounded"
                  placeholder="Meal name"
                />
              </div>
              
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                className="w-full p-2 border rounded"
                placeholder="Description"
                rows={2}
              />

              <textarea
                value={editForm.recipe || ''}
                onChange={(e) => setEditForm({...editForm, recipe: e.target.value})}
                className="w-full p-2 border rounded"
                placeholder="Recipe instructions (optional)"
                rows={4}
              />

              <div className="grid grid-cols-2 gap-2">
                <select
                  value={editForm.meal_type || ''}
                  onChange={(e) => setEditForm({...editForm, meal_type: e.target.value})}
                  className="p-2 border rounded"
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="snack">Snack</option>
                </select>
                
                <select
                  value={editForm.category || ''}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  className="p-2 border rounded"
                >
                  <option value="home_cooked">Home Cooked</option>
                  <option value="takeout">Takeout</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="delivery">Delivery</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  value={editForm.prep_time_minutes || ''}
                  onChange={(e) => setEditForm({...editForm, prep_time_minutes: parseInt(e.target.value)})}
                  className="p-2 border rounded"
                  placeholder="Prep time (minutes)"
                />
                <input
                  type="number"
                  value={editForm.cost_estimate || ''}
                  onChange={(e) => setEditForm({...editForm, cost_estimate: parseFloat(e.target.value)})}
                  className="p-2 border rounded"
                  placeholder="Cost estimate ($)"
                  step="0.01"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleCancel}
                  className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <Save className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            // View mode
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-2xl" title={option.name}>{option.emoji}</span>
                  <span className="font-semibold">{option.name}</span>
                  <span 
                    className="px-2 py-1 bg-gray-100 rounded text-xs"
                    title={getMealTypeTooltip(option.meal_type)}
                  >
                    {option.meal_type}
                  </span>
                  <span 
                    className="px-2 py-1 bg-blue-100 rounded text-xs"
                    title={getCategoryTooltip(option.category)}
                  >
                    {option.category}
                  </span>
                  {option.prep_time_minutes && (
                    <span className="text-xs text-gray-600" title="Preparation time">
                      ⏱️ {option.prep_time_minutes}min
                    </span>
                  )}
                  {option.cost_estimate && (
                    <span className="text-xs text-gray-600" title="Estimated cost">
                      💵 ${option.cost_estimate}
                    </span>
                  )}
                </div>
                {option.description && (
                  <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                )}
                {option.recipe && (
                  <button
                    onClick={() => setShowRecipe(showRecipe === option.id ? null : option.id)}
                    className="text-sm text-blue-600 hover:underline mt-1"
                  >
                    <Book className="inline w-3 h-3 mr-1" />
                    {showRecipe === option.id ? 'Hide' : 'Show'} Recipe
                  </button>
                )}
              </div>
              
              {isParent && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(option)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    title="Edit meal option"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(option.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Delete meal option"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Recipe display */}
          {showRecipe === option.id && option.recipe && (
            <div className="mt-3 p-3 bg-gray-50 rounded">
              <h4 className="font-semibold text-sm mb-2">Recipe:</h4>
              <p className="text-sm whitespace-pre-wrap">{option.recipe}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};